import React from 'react'
import ActionButton, { ActionButtonGroup } from '../ActionButton/ActionButton'
import { Spinner } from '../Loaders/Loaders'
import DocumentIcon from '@tecuity/sos-icons/react/document'
import TrashIcon from '@tecuity/sos-icons/react/trash'
import PencilIcon from '@tecuity/sos-icons/react/pencil'
import DownloadIcon from '@tecuity/sos-icons/build/react-icons/download-box'
import SignatureIcon from '@tecuity/sos-icons/react/signature'
import WarningIcon from '@tecuity/sos-icons/react/exclamation-point'
import CartIcon from '@tecuity/sos-icons/react/cart'

const WQ_ALLOW_DELETE_PRINT_MAIL = process.env.WQ_ALLOW_DELETE_PRINT_MAIL
const WQ_ALLOW_EDIT_PENDING_PAYMENT = process.env.WQ_ALLOW_EDIT_PENDING_PAYMENT
const SHOPPING_CART = process.env.SHOPPING_CART

// Converts sentence-cased Misc statuses ("Pending Review") to underscore keys
// matching the vanilla WQ's TYPE strings ("Pending_Review").
const normalizeStatus = (status) => (status || '').replace(/\s+/g, '_')

const buildActions = (callbacks) => {
  const {
    onView,
    onEditDraft,
    onDelete,
    onDeleteDraft,
    onCorrect,
    onManageSignatures,
    onSelectDownloads,
    onPayment,
    paymentDisabled,
    paymentDisabledId,
    checkoutOpen,
  } = callbacks

  const warningAction = {
    label: (row) => row.WARNING_MESSAGE,
    className: 'warning',
    onClick: () => {},
    children: <WarningIcon />,
    disabled: true,
    getIsVisible: (row) => !!row.WARNING_MESSAGE && row.WARNING_MESSAGE !== '',
  }

  const viewAction = {
    label: 'View Filing',
    className: 'primary',
    onClick: onView,
    children: <DocumentIcon />,
  }

  const editDraftAction = {
    label: 'Resume Editing',
    className: 'primary',
    onClick: onEditDraft,
    children: <PencilIcon />,
  }

  const deleteAction = {
    label: 'Delete',
    className: 'primary',
    onClick: onDelete,
    children: <TrashIcon />,
  }

  const deleteDraftAction = {
    label: 'Delete',
    className: 'primary',
    onClick: onDeleteDraft || onDelete,
    children: <TrashIcon />,
  }

  const correctAction = {
    label: 'Make a Correction',
    className: 'primary',
    onClick: onCorrect,
    children: <PencilIcon />,
  }

  const downloadsAction = {
    label: 'View Downloads',
    className: 'primary',
    onClick: onSelectDownloads,
    children: <DownloadIcon />,
    getIsVisible: (row) => !!onSelectDownloads && row.HAS_DOWNLOADS_AVAILABLE !== false,
  }

  const signaturesAction = {
    label: (row) =>
      normalizeStatus(row.TYPE || row.Status) === 'Pending_Review'
        ? 'Review Signatures'
        : 'Manage Signatures',
    className: 'primary',
    onClick: onManageSignatures,
    children: <SignatureIcon />,
    getIsVisible: (row) => !!onManageSignatures && !!row.GROUP_TOKEN,
  }

  const paymentAction = (row) => ({
    label: SHOPPING_CART ? 'Add to Cart' : 'Make Payment',
    className: 'primary',
    onClick: onPayment,
    disabled: paymentDisabled,
    children:
      paymentDisabled && paymentDisabledId === row.AMENDMENT_NUM
        ? !checkoutOpen
          ? (
            <div className="spinner-wrapper">
              <Spinner size="small" themeColor="dark-neutral" />
            </div>
          )
          : <CartIcon />
        : <CartIcon />,
  })

  return {
    warningAction,
    viewAction,
    editDraftAction,
    deleteAction,
    deleteDraftAction,
    correctAction,
    downloadsAction,
    signaturesAction,
    paymentAction,
  }
}

export const buildActionMap = (callbacks) => {
  const a = buildActions(callbacks)
  return {
    default: [a.warningAction, a.viewAction, a.downloadsAction],
    Needs_Correction: [a.warningAction, a.correctAction, a.downloadsAction],
    Rejected: [a.warningAction, a.viewAction, a.downloadsAction],
    Draft: [a.warningAction, a.editDraftAction, a.deleteDraftAction, a.downloadsAction],
    Pending_Review: [a.warningAction, a.viewAction, a.signaturesAction, a.downloadsAction],
    Approved: [a.warningAction, a.viewAction, a.downloadsAction],
    Pending_Print_and_Mail: [
      a.warningAction,
      a.viewAction,
      ...(WQ_ALLOW_DELETE_PRINT_MAIL ? [a.deleteAction] : []),
      a.downloadsAction,
    ],
    Pending_Signature: [a.warningAction, a.viewAction, a.signaturesAction],
    Pending_Online_Payment: (row) => [
      a.warningAction,
      a.paymentAction(row),
      a.viewAction,
      a.signaturesAction,
      a.downloadsAction,
      a.deleteAction,
      ...(WQ_ALLOW_EDIT_PENDING_PAYMENT ? [a.correctAction] : []),
    ],
    Static: [],
  }
}

const WorkQueueActionCell = ({
  row,
  statusKey,
  actionCellClassName,
  onView,
  onEditDraft,
  onDelete,
  onDeleteDraft,
  onCorrect,
  onManageSignatures,
  onSelectDownloads,
  onPayment,
  paymentDisabled,
  paymentDisabledId,
  checkoutOpen,
}) => {
  const callbacks = {
    onView,
    onEditDraft,
    onDelete,
    onDeleteDraft,
    onCorrect,
    onManageSignatures,
    onSelectDownloads,
    onPayment,
    paymentDisabled: paymentDisabled || false,
    paymentDisabledId: paymentDisabledId || null,
    checkoutOpen: checkoutOpen || false,
  }
  const actionMap = buildActionMap(callbacks)
  const rawStatus = row[(statusKey || 'TYPE')] || ''
  const normalizedStatus = normalizeStatus(rawStatus)
  const actionsOrFn =
    actionMap[normalizedStatus] !== undefined ? actionMap[normalizedStatus] : actionMap.default
  const actions = typeof actionsOrFn === 'function' ? actionsOrFn(row) : actionsOrFn

  if (row.NO_ACTIONS === true || !actions || !actions.length) {
    return null
  }

  return (
    <ActionButtonGroup
      buttonSize="extra-small"
      className={(actionCellClassName || '') + ' table-button'}
    >
      {actions.map((button, i) => {
        const isVisible = button.getIsVisible ? button.getIsVisible(row) : true
        const label = typeof button.label === 'function' ? button.label(row) : button.label
        return isVisible ? (
          <ActionButton
            hideLabel
            tooltip
            {...button}
            label={label}
            onClick={() => button.onClick(row)}
            key={i}
            flex
            disabled={button.disabled || false}
          />
        ) : null
      })}
    </ActionButtonGroup>
  )
}

export default WorkQueueActionCell
